import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { useApp } from "../context/AppContext";
import { useSEO } from "../hooks/useSEO";
import { Button } from "../components/ui/button";
import {
  Download, Smartphone, Apple, QrCode, Share, Plus, Sparkles, ArrowLeft,
  CheckCircle2, ShieldCheck, Zap, Globe,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = "https://bscan.app";
const INSTALL_FLAG_KEY = "bscan_install_confirmed_v1";

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "ios";
  // iPadOS 13+ reports as Mac with touch
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios";
  return "desktop";
};

// Detect Android browsers that DON'T support proper PWA install (no beforeinstallprompt
// or trigger Play Protect blocks). These users must be redirected to Chrome.
const detectBrowser = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/SamsungBrowser/i.test(ua)) return "samsung";
  if (/FBAN|FBAV|Instagram|FB_IAB/i.test(ua)) return "facebook"; // in-app browsers
  if (/TikTok|Bytedance/i.test(ua)) return "tiktok";
  if (/Line\//i.test(ua)) return "line";
  if (/EdgA|EdgiOS|Edg\//i.test(ua)) return "edge";
  if (/CriOS|Chrome/i.test(ua)) return "chrome";
  if (/Firefox|FxiOS/i.test(ua)) return "firefox";
  if (/Safari/i.test(ua)) return "safari";
  return "other";
};

const useFormattedCount = (n) => useMemo(() => (n || 0).toLocaleString("en-US"), [n]);

export default function InstallPage({ forcePlatform = null }) {
  const { language, setLang } = useApp();
  const es = language === "es";

  useSEO({
    title: es ? "Instalar BScanIA — Tu mentor IA en tu bolsillo" : "Install BScanIA — Your AI mentor in your pocket",
    description: es
      ? "Instala BScanIA en Android, iPhone o computadora. Acceso instantáneo, sin tiendas, sin esperas. Ya nos eligieron más de 1,200 profesionales."
      : "Install BScanIA on Android, iPhone or computer. Instant access, no app stores, no waiting. Over 1,200 professionals already on board.",
    canonical: "https://bscan.app/install",
    lang: language,
    ogImage: "https://bscan.app/og-image.jpg",
    keywords: "BScanIA, install, PWA, AI mentor app, professional mentorship app, Android, iPhone, install BScanIA",
  });

  const [platform, setPlatform] = useState("desktop");
  const [browser, setBrowser] = useState("unknown");
  const [installCount, setInstallCount] = useState(1247);
  const [displayCount, setDisplayCount] = useState(1247);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [iosStep, setIosStep] = useState(0); // 0=intro, 1=share, 2=add, 3=done
  const [socialProof, setSocialProof] = useState(null);
  const lastConfirmedRef = useRef(false);

  // Detect platform on mount (or honor forced/query override for marketing links)
  useEffect(() => {
    const qsPlatform = new URLSearchParams(window.location.search).get("platform");
    const override = (forcePlatform || qsPlatform || "").toLowerCase();
    if (["android", "ios", "iphone", "desktop"].includes(override)) {
      setPlatform(override === "iphone" ? "ios" : override);
    } else {
      setPlatform(detectPlatform());
    }
    setBrowser(detectBrowser());
  }, [forcePlatform]);

  // Fetch install count
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/stats/installs`);
        if (alive) setInstallCount(res.data?.count || 1247);
      } catch (e) { /* keep base */ void e; }
    })();
    return () => { alive = false; };
  }, []);

  // Fetch live social proof — decisions processed lifetime + weekly people helped
  useEffect(() => {
    axios.get(`${API}/stats/social-proof`)
      .then((r) => setSocialProof(r.data))
      .catch(() => { /* silent */ });
  }, []);

  // Animate counter on mount (from base-15 to actual count)
  useEffect(() => {
    const target = installCount;
    const start = Math.max(target - 15, 0);
    setDisplayCount(start);
    let raf;
    let current = start;
    const step = () => {
      if (current < target) {
        current = Math.min(target, current + Math.max(1, Math.ceil((target - current) / 10)));
        setDisplayCount(current);
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [installCount]);

  // Listen for Android install prompt
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      confirmInstall("android");
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    // Check standalone
    const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    if (standalone || window.navigator.standalone) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate QR code for desktop view
  useEffect(() => {
    QRCode.toDataURL(`${APP_URL}/install`, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
      color: { dark: "#FFFFFF", light: "#0A0A0A00" },
    }).then(setQrUrl).catch(() => {});
  }, []);

  const confirmInstall = async (plat) => {
    if (lastConfirmedRef.current) return;
    if (localStorage.getItem(INSTALL_FLAG_KEY)) return;
    lastConfirmedRef.current = true;
    try {
      const res = await axios.post(`${API}/stats/installs/confirm`, {
        platform: plat,
        source: new URLSearchParams(window.location.search).get("utm_source") || "",
      });
      localStorage.setItem(INSTALL_FLAG_KEY, "1");
      if (res.data?.count) setInstallCount(res.data.count);
    } catch (e) { /* silent */ void e; }
  };

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        // appinstalled event will fire and call confirmInstall
      }
      setDeferredPrompt(null);
    } else {
      // No prompt available — fallback to instructions
      alert(es
        ? "Para instalar: abre BScanIA en Chrome y selecciona 'Añadir a pantalla principal' en el menú de Chrome."
        : "To install: open BScanIA in Chrome and select 'Add to Home Screen' from the Chrome menu.");
    }
  };

  const handleIosStart = () => setIosStep(1);
  const handleIosDone = () => {
    confirmInstall("ios");
    setIosStep(3);
  };

  const fmtCount = useFormattedCount(displayCount);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      {/* Background ornaments */}
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-[#006CFF]/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#7C3AED]/15 rounded-full blur-[120px] pointer-events-none" />

      {/* Top bar */}
      <header className="relative z-10 border-b border-white/10 backdrop-blur bg-black/30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="install-back-home">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <img src="/brand-logo.png" alt="BScanIA" className="h-10 w-auto" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white/70" data-testid="install-lang">
                <Globe className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                <span className="font-mono text-xs">{language.toUpperCase()}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1A1A1A] border-white/10 text-white">
              <DropdownMenuItem onClick={() => setLang("en")}>English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("es")}>Español</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-12 pb-24">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#006CFF]/40 bg-[#006CFF]/10 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#006CFF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#006CFF]">
              {es ? "Instalación instantánea" : "Instant install"}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5" data-testid="install-hero-title">
            {es ? (
              <>Tu mentor IA, <span className="bg-gradient-to-r from-[#006CFF] to-[#7C3AED] bg-clip-text text-transparent">en tu bolsillo</span>.</>
            ) : (
              <>Your AI mentor, <span className="bg-gradient-to-r from-[#006CFF] to-[#7C3AED] bg-clip-text text-transparent">in your pocket</span>.</>
            )}
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-8">
            {es
              ? "Instala BScanIA en segundos. Sin tiendas, sin descargas pesadas, sin esperas. Funciona como una app nativa."
              : "Install BScanIA in seconds. No app stores, no heavy downloads, no waiting. Works like a native app."}
          </p>

          {/* Install counter — investor-friendly traction display */}
          <div
            data-testid="install-counter"
            className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm"
          >
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#006CFF] to-[#7C3AED] border-2 border-[#0A0A0A] flex items-center justify-center text-[10px] font-bold">B</div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#FF6B6B] border-2 border-[#0A0A0A] flex items-center justify-center text-[10px] font-bold">M</div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#10B981] to-[#006CFF] border-2 border-[#0A0A0A] flex items-center justify-center text-[10px] font-bold">+</div>
            </div>
            <div className="text-left">
              <div className="font-mono text-2xl sm:text-3xl font-bold text-white tabular-nums" data-testid="install-counter-number">
                {fmtCount}+
              </div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-white/50 font-mono">
                {es ? "profesionales instalados" : "professionals installed"}
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 ml-2 pl-4 border-l border-white/10">
              <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-[11px] font-mono text-[#10B981] uppercase">{es ? "En vivo" : "Live"}</span>
            </div>
          </div>
          {(socialProof?.show_decisions || socialProof?.show) && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="install-social-proof">
              {socialProof?.show_decisions && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#006CFF]/8 border border-[#006CFF]/25">
                  <Sparkles className="w-3 h-3 text-[#006CFF]" strokeWidth={2} />
                  <span className="text-[#9FC9FF] text-[11px] font-mono uppercase tracking-[0.15em]">
                    {es
                      ? `${socialProof.decisions_total.toLocaleString("es")} decisiones procesadas con B`
                      : `${socialProof.decisions_total.toLocaleString("en")} decisions processed with B`}
                  </span>
                </div>
              )}
              {socialProof?.show && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00FF88]/8 border border-[#00FF88]/25">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF88] opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00FF88]" />
                  </span>
                  <span className="text-[#9FFFD0] text-[11px] font-mono uppercase tracking-[0.15em]">
                    {es
                      ? `+${socialProof.count_7d} ayudados esta semana`
                      : `+${socialProof.count_7d} helped this week`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Platform-specific install card */}
        <div className="mt-14 max-w-2xl mx-auto">
          {installed ? (
            <InstalledCard es={es} />
          ) : platform === "android" && !["chrome", "edge"].includes(browser) ? (
            <OpenInChromeCard es={es} browser={browser} />
          ) : platform === "android" ? (
            <AndroidCard es={es} onInstall={handleAndroidInstall} hasPrompt={!!deferredPrompt} />
          ) : platform === "ios" ? (
            <IosCard es={es} step={iosStep} onStart={handleIosStart} onAdvance={setIosStep} onDone={handleIosDone} />
          ) : (
            <DesktopCard es={es} qrUrl={qrUrl} />
          )}

          {/* Platform switcher (so users can preview other flows) */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40 mr-2">
              {es ? "Otro dispositivo:" : "Other device:"}
            </span>
            <PlatformChip active={platform === "android"} onClick={() => setPlatform("android")} icon={<Smartphone className="w-3 h-3" />} label="Android" />
            <PlatformChip active={platform === "ios"} onClick={() => setPlatform("ios")} icon={<Apple className="w-3 h-3" />} label="iPhone" />
            <PlatformChip active={platform === "desktop"} onClick={() => setPlatform("desktop")} icon={<QrCode className="w-3 h-3" />} label={es ? "Escritorio" : "Desktop"} />
          </div>
        </div>

        {/* Benefits strip */}
        <div className="mt-20 grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
          <BenefitCard
            icon={<Zap className="w-5 h-5 text-[#006CFF]" />}
            title={es ? "0 segundos de espera" : "0 seconds wait"}
            body={es ? "Sin App Store. Se instala desde tu navegador en un toque." : "No App Store. Installs from your browser in one tap."}
          />
          <BenefitCard
            icon={<ShieldCheck className="w-5 h-5 text-[#10B981]" />}
            title={es ? "Privado y seguro" : "Private & secure"}
            body={es ? "Tus conversaciones nunca alimentan modelos públicos." : "Your conversations never feed public models."}
          />
          <BenefitCard
            icon={<Sparkles className="w-5 h-5 text-[#7C3AED]" />}
            title={es ? "Igual que una app nativa" : "Feels native"}
            body={es ? "Ícono en tu pantalla, modo pantalla completa, notificaciones push." : "Icon on home screen, full-screen mode, push notifications."}
          />
        </div>

        {/* Footer micro-copy */}
        <p className="text-center text-[11px] font-mono uppercase tracking-[0.15em] text-white/30 mt-16">
          {es ? "BScanIA · Mentor IA personal · Instalable como PWA" : "BScanIA · Personal AI mentor · Installable as PWA"}
        </p>
      </main>
    </div>
  );
}

/* ============= Sub-components ============= */

const PlatformChip = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-mono uppercase tracking-[0.1em] transition-all
      ${active ? "border-[#006CFF]/60 bg-[#006CFF]/15 text-white" : "border-white/10 bg-white/[0.02] text-white/50 hover:text-white/80 hover:border-white/20"}`}
    data-testid={`install-chip-${label.toLowerCase()}`}
  >
    {icon}
    <span>{label}</span>
  </button>
);

const BenefitCard = ({ icon, title, body }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 text-left">
    <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-3">{icon}</div>
    <div className="text-sm font-semibold text-white mb-1">{title}</div>
    <div className="text-sm text-white/60 leading-snug">{body}</div>
  </div>
);

const AndroidCard = ({ es, onInstall, hasPrompt }) => (
  <div
    data-testid="install-android-card"
    className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0E0E12] to-[#0A0A0A] p-8 sm:p-10 shadow-[0_0_60px_-30px_rgba(0,108,255,0.5)]"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-[#006CFF]/15 border border-[#006CFF]/30 flex items-center justify-center">
        <Smartphone className="w-5 h-5 text-[#006CFF]" />
      </div>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#006CFF]">Android</div>
        <div className="text-lg font-semibold">{es ? "Instalación en 1 toque" : "1-tap install"}</div>
      </div>
    </div>
    <p className="text-sm text-white/60 mb-7">
      {es
        ? "Toca el botón abajo, confirma y BScanIA aparecerá en tu pantalla principal como una app nativa."
        : "Tap the button below, confirm, and BScanIA will appear on your home screen like a native app."}
    </p>
    <button
      onClick={onInstall}
      className="block w-full max-w-xs mx-auto transition-transform active:scale-[0.98] hover:scale-[1.02]"
      data-testid="install-android-button"
      aria-label={es ? "Instalar en Android" : "Install on Android"}
    >
      <img
        src={es ? "/store-badges/style1-android-es.png" : "/store-badges/style1-android-en.png"}
        alt={es ? "Instalar en Android" : "Install on Android"}
        className="w-full h-auto rounded-lg"
      />
    </button>
    {!hasPrompt && (
      <p className="text-center text-[11px] text-white/40 mt-4 font-mono uppercase tracking-wider">
        {es ? "Abre desde Chrome para mejor experiencia" : "Open in Chrome for the best experience"}
      </p>
    )}
  </div>
);

const IosCard = ({ es, step, onStart, onAdvance, onDone }) => (
  <div
    data-testid="install-ios-card"
    className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0E0E12] to-[#0A0A0A] p-8 sm:p-10 shadow-[0_0_60px_-30px_rgba(124,58,237,0.5)]"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/15 border border-[#7C3AED]/30 flex items-center justify-center">
        <Apple className="w-5 h-5 text-[#7C3AED]" />
      </div>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#7C3AED]">iPhone · iOS</div>
        <div className="text-lg font-semibold">{es ? "Instalar en 3 pasos" : "Install in 3 steps"}</div>
      </div>
    </div>

    {step === 0 && (
      <>
        <p className="text-sm text-white/60 mb-7">
          {es
            ? "iOS requiere agregar BScanIA desde Safari. Tarda menos de 10 segundos."
            : "iOS requires adding BScanIA from Safari. Takes less than 10 seconds."}
        </p>
        <button onClick={onStart} className="block w-full max-w-xs mx-auto transition-transform active:scale-[0.98] hover:scale-[1.02]" data-testid="install-ios-start">
          <img
            src={es ? "/store-badges/style1-ios-es.png" : "/store-badges/style1-ios-en.png"}
            alt={es ? "Instalar en iPhone" : "Install on iPhone"}
            className="w-full h-auto rounded-lg"
          />
        </button>
      </>
    )}

    {step >= 1 && step <= 2 && (
      <div className="space-y-4" data-testid="install-ios-steps">
        <IosStep
          n={1}
          active={step === 1}
          done={step > 1}
          icon={<Share className="w-5 h-5" />}
          title={es ? "Toca el ícono Compartir" : "Tap the Share icon"}
          body={es ? "Es el cuadrado con la flecha hacia arriba, en la barra inferior de Safari." : "It's the square with an up-arrow at the bottom of Safari."}
          onNext={() => onAdvance(2)}
          ctaLabel={es ? "Ya lo toqué" : "Tapped it"}
        />
        <IosStep
          n={2}
          active={step === 2}
          done={step > 2}
          icon={<Plus className="w-5 h-5" />}
          title={es ? "Elige 'Añadir a pantalla de inicio'" : "Choose 'Add to Home Screen'"}
          body={es ? "Baja en el menú hasta encontrarlo. Toca 'Añadir' arriba a la derecha." : "Scroll down in the menu, tap 'Add' in the top-right."}
          onNext={() => onAdvance(3) || onDone()}
          ctaLabel={es ? "Ya lo añadí" : "Added"}
        />
        {step >= 2 && (
          <button
            onClick={onDone}
            className="w-full mt-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#006CFF] to-[#7C3AED] text-white font-semibold text-sm hover:opacity-90 transition"
            data-testid="install-ios-done"
          >
            {es ? "¡Listo, lo instalé!" : "Done, I installed it!"}
          </button>
        )}
      </div>
    )}

    {step === 3 && (
      <div className="text-center py-4" data-testid="install-ios-success">
        <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
        </div>
        <div className="text-lg font-semibold mb-2">{es ? "Bienvenido a BScanIA" : "Welcome to BScanIA"}</div>
        <p className="text-sm text-white/60 mb-5">
          {es ? "Busca el ícono de BScanIA en tu pantalla principal y ábrelo desde ahí." : "Look for the BScanIA icon on your home screen and open it from there."}
        </p>
        <Link
          to="/dashboard"
          className="inline-block px-6 py-2.5 rounded-xl bg-white text-black font-semibold text-sm hover:bg-white/90 transition"
        >
          {es ? "Continuar en el navegador" : "Continue in browser"}
        </Link>
      </div>
    )}
  </div>
);

const IosStep = ({ n, active, done, icon, title, body, onNext, ctaLabel }) => (
  <div
    className={`rounded-xl border p-4 transition-all flex gap-4 items-start
      ${done ? "border-[#10B981]/40 bg-[#10B981]/[0.04]" : active ? "border-[#7C3AED]/40 bg-[#7C3AED]/[0.06]" : "border-white/10 bg-white/[0.02] opacity-60"}`}
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border
      ${done ? "bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]" : active ? "bg-[#7C3AED]/15 border-[#7C3AED]/40 text-[#7C3AED]" : "bg-white/5 border-white/10 text-white/40"}`}>
      {done ? <CheckCircle2 className="w-5 h-5" /> : icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-xs font-mono uppercase tracking-[0.15em] text-white/40 mb-1">
        {`Step ${n}`}
      </div>
      <div className="text-sm font-semibold mb-1">{title}</div>
      <div className="text-xs text-white/60 leading-snug">{body}</div>
      {active && !done && (
        <button
          onClick={onNext}
          className="mt-3 px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-semibold transition"
        >
          {ctaLabel}
        </button>
      )}
    </div>
  </div>
);

const DesktopCard = ({ es, qrUrl }) => (
  <div
    data-testid="install-desktop-card"
    className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0E0E12] to-[#0A0A0A] p-8 sm:p-10 shadow-[0_0_60px_-30px_rgba(16,185,129,0.4)]"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center">
        <QrCode className="w-5 h-5 text-[#10B981]" />
      </div>
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#10B981]">{es ? "Escritorio" : "Desktop"}</div>
        <div className="text-lg font-semibold">{es ? "Escanea con tu teléfono" : "Scan with your phone"}</div>
      </div>
    </div>
    <p className="text-sm text-white/60 mb-7">
      {es
        ? "BScanIA es una app móvil. Escanea este código QR con la cámara de tu teléfono y empieza la instalación."
        : "BScanIA is a mobile app. Scan this QR code with your phone camera and start the install."}
    </p>
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center justify-center">
        {qrUrl ? (
          <img src={qrUrl} alt="QR install BScanIA" className="w-[200px] h-[200px]" data-testid="install-qr-image" />
        ) : (
          <div className="w-[200px] h-[200px] animate-pulse bg-white/5 rounded" />
        )}
      </div>
      <div className="flex-1 text-left">
        <div className="text-sm font-semibold mb-2">bscan.app/install</div>
        <p className="text-xs text-white/50 leading-relaxed mb-4">
          {es
            ? "También puedes instalar BScanIA directamente en tu computadora desde Chrome o Edge. Busca el ícono ⊕ en la barra de direcciones."
            : "You can also install BScanIA directly on your computer from Chrome or Edge. Look for the ⊕ icon in the address bar."}
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-semibold transition"
        >
          <Download className="w-3.5 h-3.5" />
          {es ? "Abrir en este navegador" : "Open in this browser"}
        </Link>
      </div>
    </div>
  </div>
);

const OpenInChromeCard = ({ es, browser }) => {
  const browserLabel = {
    samsung: "Samsung Internet",
    facebook: "Facebook / Instagram",
    tiktok: "TikTok",
    line: "Line",
    firefox: "Firefox",
    other: es ? "este navegador" : "this browser",
  }[browser] || (es ? "este navegador" : "this browser");

  const openInChrome = () => {
    const url = "https://bscan.app/install";
    // Try Chrome intent (works on Android)
    const intent = `intent://bscan.app/install#Intent;scheme=https;package=com.android.chrome;end`;
    try {
      window.location.href = intent;
      setTimeout(() => { window.location.href = url; }, 800);
    } catch (e) {
      void e;
      window.location.href = url;
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText("https://bscan.app/install");
      alert(es ? "Link copiado. Pégalo en Chrome." : "Link copied. Paste it in Chrome.");
    } catch (e) { void e; }
  };

  return (
    <div
      data-testid="install-open-chrome-card"
      className="rounded-2xl border border-[#FFB800]/30 bg-gradient-to-br from-[#FFB800]/[0.05] to-[#0A0A0A] p-8 sm:p-10 shadow-[0_0_60px_-30px_rgba(255,184,0,0.4)]"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#FFB800]/15 border border-[#FFB800]/40 flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-[#FFB800]" />
        </div>
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#FFB800]">
            {es ? "Abre en Chrome" : "Open in Chrome"}
          </div>
          <div className="text-lg font-semibold">
            {es ? `Detectamos ${browserLabel}` : `${browserLabel} detected`}
          </div>
        </div>
      </div>
      <p className="text-sm text-white/70 mb-5 leading-relaxed">
        {es
          ? `${browserLabel} no soporta la instalación rápida de apps web (PWA). Para evitar bloqueos de Play Protect y aprovechar la instalación en 1 toque, abre BScanIA en Google Chrome.`
          : `${browserLabel} doesn't support fast web app (PWA) install. To avoid Play Protect blocks and get 1-tap install, open BScanIA in Google Chrome.`}
      </p>

      <div className="space-y-4 mb-6">
        <Step n="1" body={es ? "Toca el menú (⋮) de tu navegador actual" : "Tap your browser's menu (⋮)"} />
        <Step n="2" body={es ? "Busca 'Abrir en Chrome' o 'Abrir con otro navegador'" : "Find 'Open in Chrome' or 'Open with another browser'"} />
        <Step n="3" body={es ? "Selecciona Google Chrome y vuelve aquí" : "Select Google Chrome and come back here"} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={openInChrome}
          className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-[#006CFF] to-[#7C3AED] text-white font-semibold text-sm hover:opacity-90 transition"
          data-testid="install-open-chrome-btn"
        >
          {es ? "Intentar abrir Chrome" : "Try opening Chrome"}
        </button>
        <button
          onClick={copyLink}
          className="px-5 py-3 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white text-sm font-medium transition"
          data-testid="install-copy-link-btn"
        >
          {es ? "Copiar enlace" : "Copy link"}
        </button>
      </div>

      <p className="text-[10px] text-white/40 mt-4 font-mono uppercase tracking-wider text-center">
        bscan.app/install
      </p>
    </div>
  );
};

const Step = ({ n, body }) => (
  <div className="flex gap-3 items-start">
    <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-xs font-mono text-white/60">{n}</div>
    <p className="text-sm text-white/70 leading-snug pt-1">{body}</p>
  </div>
);

const InstalledCard = ({ es }) => (  <div
    data-testid="install-installed-card"
    className="rounded-2xl border border-[#10B981]/30 bg-[#10B981]/[0.04] p-8 sm:p-10 text-center"
  >
    <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center mx-auto mb-4">
      <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
    </div>
    <div className="text-xl font-semibold mb-2">{es ? "BScanIA ya está instalada" : "BScanIA is installed"}</div>
    <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">
      {es ? "Detectamos que ya tienes la app. Busca el ícono en tu pantalla principal." : "We detected the app is already installed. Look for the icon on your home screen."}
    </p>
    <Link
      to="/dashboard"
      className="inline-block px-6 py-2.5 rounded-xl bg-white text-black font-semibold text-sm hover:bg-white/90 transition"
    >
      {es ? "Ir al panel" : "Go to dashboard"}
    </Link>
  </div>
);
